import { type EventResponse, type ProfileResponse } from '@lifetimeline/shared';

import QRCode from 'qrcode';

import { profileHandle, profileUrl } from './branding.js';

/**
 * 공유용 **한 장**.
 *
 * 전부를 담지 않는다. 다 보여주면 들어올 이유가 사라진다 — 스토리에서 인생
 * 전체를 봤는데 굳이 주소를 칠 사람은 없다. 들어가는 만큼만 그리고, 더
 * 있다는 건 **축이 아래로 흐려지는 것**으로 말한다(숫자로 적지 않는다).
 *
 * 서버를 안 쓴다 — 브라우저 캔버스로 그린다. 사진이 같은 출처(`/uploads/…`)라
 * 캔버스가 안 더럽혀지고, 그려 주는 서버가 없으니 비용도 안 든다.
 */

/** 스토리(9:16)와 피드·카톡(1:1). 고르는 건 사람이고, 담기는 양은 자리가 정한다 */
export type ShareRatio = 'square' | 'story';

/**
 * 비율마다 **어느 폭의 화면을 찍은 것인지**가 다르다.
 *
 * 둘 다 1080으로 내보내지만, 1:1은 세로가 절반이라 폰 폭(390)을 그대로
 * 키우면 기록이 세 줄밖에 안 들어간다 — 타임라인 서비스의 그림이 아니다.
 * 그래서 1:1이 더 넓은 화면을 찍은 것으로 둔다.
 *
 * 여백·글씨·점의 **비율은 어느 쪽이든 똑같다** — 큰 화면으로 보느냐 작은
 * 화면으로 보느냐의 차이일 뿐이다. 이 숫자를 키우면 전체가 작아지고 그만큼
 * 기록이 더 들어간다.
 */
const SIZES: Record<ShareRatio, { width: number; height: number; viewport: number }> = {
  square: { width: 1080, height: 1080, viewport: 600 },
  story: { width: 1080, height: 1920, viewport: 470 },
};

/**
 * 한 장에 담는 기록 수의 **최대**. 실제로는 자리가 되는 만큼만 담는다.
 *
 * 넉넉히 둔다 — 자르는 건 자리 계산이 하고, 이 숫자는 "아무리 작게 그려도
 * 이 이상은 안 담는다"는 선일 뿐이다.
 */
const MAX_SHOWN_EVENTS = 16;
/** 취향은 격자 한 줄(3칸)까지. 더 보여줄 바에 기록을 한 줄 더 담는다 */
const SHOWN_PICKS = 3;
/**
 * 취향 격자를 넣으려면 기록이 **최소 이만큼**은 남아야 한다.
 *
 * 1:1은 세로가 절반이라 격자를 넣으면 기록이 한두 줄만 남는다. 그러면
 * 타임라인 서비스의 그림이 아니게 되므로, 자리가 모자라면 격자를 뺀다.
 */
const MIN_EVENTS_WITH_PICKS = 5;

/**
 * 색은 **화면에서 그대로 읽어 온다.**
 *
 * 예전에는 어두운 팔레트를 여기 적어 뒀다. 사람이 테마를 고를 수 있게 된
 * 뒤로는 그게 거짓말이 된다 — 밝게 쓰는 사람이 어두운 카드를 받는다.
 * 색을 한 벌 더 적어 두면 `tokens.css`를 고칠 때 **여기만 옛 색**으로 남는
 * 문제도 따라온다.
 *
 * `getComputedStyle`로 `:root`의 토큰을 읽으면 테마를 따라가고, 토큰이
 * 바뀌어도 저절로 맞는다. 못 읽으면(캔버스를 화면 밖에서 그리는 경우 등)
 * 어두운 값으로 떨어진다 — 이 그림이 제일 많이 가는 자리가 스토리라서다.
 */
function palette() {
  const root = getComputedStyle(document.documentElement);
  const read = (name: string, fallback: string): string => {
    const value = root.getPropertyValue(name).trim();
    return value === '' ? fallback : value;
  };
  return {
    ink: read('--ink', '#f2f2f2'),
    inkSoft: read('--ink-soft', '#c9c9c9'),
    muted: read('--muted', '#8f8f8f'),
    bg: read('--bg', '#0d0d0d'),
    line: read('--line', '#2a2a2a'),
    soft: read('--soft', '#1c1c1c'),
    scrimMid: read('--scrim-mid', 'rgba(0,0,0,0.66)'),
    scrimStrong: read('--scrim-strong', 'rgba(0,0,0,0.82)'),
  };
}

const BODY = "'Pretendard Variable', Pretendard, system-ui, sans-serif";
const YEAR = "'Silkscreen', monospace";

/**
 * 로고는 **글자에 따라 글씨가 다르다**(`Layout.module.css`와 같은 규칙).
 *
 * ⚠️ 한글 글씨(Neo둥근모)는 **로고 세 글자만 남기고 잘라 둔 628바이트**라
 * 로마자가 아예 없다. 둘 다 그것으로 찍었더니 영어로 열었을 때 `LATARA`가
 * 픽셀도 본문도 아닌 것으로 떨어졌다 — 캔버스는 없는 글자를 조용히 대체
 * 글씨로 그려서 터지지도 않는다.
 *
 * 언어가 아니라 글자를 본다 — 나중에 다른 말을 더해도 그 이름이 로마자면
 * 픽셀(Silkscreen)로 나가야 맞다.
 */
function logoFont(appName: string): { family: string; tracking: string } {
  return /[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(appName)
    ? /* 한글 픽셀 글자는 이미 네모가 꽉 차 있어 덜 벌린다 */
      { family: `'NeoDunggeunmo', ${BODY}`, tracking: '0.04em' }
    : /* 로마자 픽셀 글자는 붙으면 덩어리로 뭉개진다 */
      { family: YEAR, tracking: '0.08em' };
}

export interface ShareCardPick {
  imageUrl?: string;
  categoryName: string;
  itemName: string;
}

export interface ShareCardInput {
  ratio: ShareRatio;
  profile: ProfileResponse;
  events: readonly EventResponse[];
  /** 주제 id → 색. 축에 찍는 점이 화면과 같은 색이어야 같은 서비스로 보인다 */
  topicColors: ReadonlyMap<string, string>;
  picks: readonly ShareCardPick[];
  /** `2024.09 ~ 현재 (2년)` 같은 줄을 만드는 쪽. 말은 화면이 안다 */
  periodText: (event: EventResponse) => string | null;
  /**
   * `인연 12명` 같은 줄. **아직 못 읽었으면 `null`**이다(0명은 그대로 찍는다).
   *
   * 숫자가 아니라 **다 만들어진 말**을 받는다 — 세는 말은 언어마다 다르고
   * (`connection.count`), 그 말은 화면이 안다.
   *
   * ⚠️ 받은 요청 뱃지는 **안 넣는다.** 나한테만 뜻이 있는 숫자라, 남이 보는
   * 그림에 있으면 뜻도 모르면서 내 사정만 흘린다.
   */
  connectionText: string | null;
  /**
   * 바닥에 찍는 서비스 이름(`common.appName`).
   *
   * ⚠️ 여기에 `'라타라'`를 박아 두면 **영어로 열어도 한글이 나온다**(실제로
   * 그랬다). 영어에서는 `LATARA`다 — 같은 이름의 다른 표기라 번역이 아니라
   * 화면이 쓰는 그 값을 그대로 받아야 한다.
   */
  appName: string;
}

export async function drawShareCard(input: ShareCardInput): Promise<Blob> {
  const { width: WIDTH, height: HEIGHT, viewport } = SIZES[input.ratio];
  /*
   * 실제 화면을 **그대로 키우는 배율**.
   *
   * 눈대중으로 키웠더니 제목이 연도보다 커져 읽는 순서가 뒤집혔다 — 실제
   * 화면은 연도(15)가 제목(13)보다 크다. 축을 훑는 화면이라 연도가 먼저
   * 읽히게 일부러 그렇게 둔 것이다.
   *
   * ⚠️ 아래 `px(...)`에 들어가는 숫자는 전부 **실제 CSS 값**이다.
   * `tokens.css`·`Timeline.module.css`·`PickGrid.module.css`를 고치면 여기도 같이 고친다.
   */
  const px = (cssPx: number): number => Math.round((cssPx * WIDTH) / viewport);
  const {
    ink: INK,
    inkSoft: INK_SOFT,
    muted: MUTED,
    bg: BG,
    line: LINE,
    soft: SOFT,
    scrimMid: SCRIM_MID,
    scrimStrong: SCRIM_STRONG,
  } = palette();
  /** 축이 흐려지며 사라지는 길이. "아래로 더 있다"는 말을 글자 없이 한다 */
  const FADE = px(30);

  /*
   * ⚠️ 글꼴을 **쓸 모양 그대로 불러 둔다.**
   *
   * `document.fonts.ready`만으로는 모자랐다 — 그건 "지금 걸려 있는 내려받기가
   * 끝났나"일 뿐이라, 화면에 안 쓰이는 굵기·크기는 아직 없을 수 있다. 캔버스는
   * 없는 글꼴을 조용히 대체 글꼴로 그리고, 한 번 그려진 그림은 나중에 글꼴이
   * 와도 안 고쳐진다. 실제로 연도가 픽셀 글씨가 아니라 민짜로 나왔다.
   */
  await Promise.all([
    document.fonts.load(`${px(15)}px ${YEAR}`, '0123456789'),
    document.fonts.load(`${px(16)}px ${logoFont(input.appName).family}`, input.appName),
    document.fonts.load(`700 ${px(19)}px ${BODY}`),
    document.fonts.load(`700 ${px(13)}px ${BODY}`),
    document.fonts.load(`600 ${px(13)}px ${BODY}`),
    document.fonts.load(`700 ${px(19)}px ${BODY}`),
    document.fonts.load(`${px(13)}px ${BODY}`),
    document.fonts.load(`${px(11)}px ${BODY}`),
  ]);
  await document.fonts.ready;

  const canvas = document.createElement('canvas');
  canvas.width = WIDTH;
  canvas.height = HEIGHT;
  const ctx = canvas.getContext('2d');
  if (ctx === null) {
    throw new Error('캔버스를 못 엽니다');
  }

  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, WIDTH, HEIGHT);

  /* 화면과 같은 좌우 여백(`Layout`의 `padding: 0 var(--s4)`) */
  const left = px(16);
  const contentWidth = WIDTH - left * 2;

  // ── 머리: 사진·이름·주소 (`.profileHead`) ──
  let y = px(24);
  const avatarSize = px(64);
  const avatar =
    input.profile.imageUrl === undefined ? null : await loadImage(input.profile.imageUrl);
  ctx.save();
  ctx.beginPath();
  ctx.arc(left + avatarSize / 2, y + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
  ctx.clip();
  if (avatar === null) {
    ctx.fillStyle = LINE;
    ctx.fillRect(left, y, avatarSize, avatarSize);
    ctx.fillStyle = INK;
    ctx.font = `700 ${Math.round(avatarSize * 0.4)}px ${BODY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(input.profile.userName.slice(0, 1), left + avatarSize / 2, y + avatarSize / 2);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  } else {
    cover(ctx, avatar, left, y, avatarSize, avatarSize);
  }
  ctx.restore();

  const textLeft = left + avatarSize + px(16); // .profileHead의 gap
  const textWidth = WIDTH - textLeft - left;
  /*
   * 이름과 @아이디는 **한 줄**이다(`.profileNameRow`). 밑선을 맞춰 찍으므로
   * 둘 다 같은 `y`에 둔다 — 캔버스의 기본 기준선이 곧 baseline이다.
   *
   * 상단은 @아이디까지만. 풀 주소는 바닥에 한 번 나오면 충분하다.
   */
  const handleText = profileHandle(input.profile.handle);
  ctx.font = `${px(13)}px ${BODY}`; // .profileHandle
  const handleWidth = ctx.measureText(handleText).width + px(8); // gap --s2
  ctx.fillStyle = INK;
  ctx.font = `700 ${px(19)}px ${BODY}`; // --t-h2
  /* 길면 **이름이** 줄어든다 — 아이디는 주소라 잘리면 못 쓴다 */
  const nameText = clip(ctx, input.profile.userName, textWidth - handleWidth);
  const nameWidth = ctx.measureText(nameText).width;
  ctx.fillText(nameText, textLeft, y + px(26));
  ctx.fillStyle = MUTED;
  ctx.font = `${px(13)}px ${BODY}`;
  ctx.fillText(handleText, textLeft + nameWidth + px(8), y + px(26));

  /* 인연 수는 이름 줄 **아래**(`.connectionCount`) */
  if (input.connectionText !== null) {
    ctx.fillStyle = INK_SOFT;
    ctx.font = `600 ${px(13)}px ${BODY}`;
    ctx.fillText(clip(ctx, input.connectionText, textWidth), textLeft, y + px(48));
  }

  y += avatarSize;
  if (input.profile.bio !== undefined && input.profile.bio !== '') {
    /*
     * 한 줄 소개는 **위아래로 떨어뜨린다.** 사진 바로 밑에 붙이면 이름 칸의
     * 셋째 줄처럼 읽히고, 타임라인에 붙이면 첫 기록의 설명처럼 읽힌다.
     * 실제 화면에서는 그 사이에 버튼과 탭이 있어 저절로 떨어져 있다.
     */
    y += px(24); // .profileHead의 margin-bottom
    ctx.fillStyle = INK_SOFT; // .bio
    ctx.font = `${px(13)}px ${BODY}`;
    ctx.fillText(clip(ctx, input.profile.bio, contentWidth), left, y);
    y += px(32);
  } else {
    y += px(24);
  }

  // ── 축 위의 기록 (`Timeline.module.css`) ──
  const yearColumn = px(56); // --year-col
  const gap = px(8); // --s2
  const dotSize = px(9);
  const axis = left + yearColumn + gap + dotSize / 2;
  const bodyLeft = left + yearColumn + gap + px(10) + gap; // 점 칸(10px) 다음
  /** `.row`의 padding(8 위아래) + 줄 높이 */
  const rowHeight = (period: string | null): number => (period === null ? px(26) : px(26) + px(16));

  const shown = input.events.slice(0, MAX_SHOWN_EVENTS).map((event) => ({
    event,
    period: input.periodText(event),
  }));

  /*
   * 바닥(주소·QR)과 격자가 쓸 자리를 **먼저 떼어 놓고**, 남는 만큼만 기록을
   * 담는다. 개수를 고정해 두면 기간이 붙은 기록이 많을 때 — 기간은 줄을 하나
   * 더 쓴다 — 바닥을 덮는다.
   */
  const qrSize = px(68);
  const footerTop = HEIGHT - px(24) - qrSize;
  const pickCells = Math.min(input.picks.length, SHOWN_PICKS);
  const pickGap = px(8); // .grid의 gap
  const pickSize = (contentWidth - pickGap * (SHOWN_PICKS - 1)) / SHOWN_PICKS;
  const pickHeight = pickSize * 1.25; // .cell의 4:5
  const fits = (withPicks: boolean): number =>
    footerTop - px(24) - (withPicks ? pickHeight + px(24) : 0) - FADE - y;

  const take = (budget: number): number => {
    let used = 0;
    let count = 0;
    for (const row of shown) {
      const next = used + rowHeight(row.period);
      if (next > budget) {
        break;
      }
      used = next;
      count += 1;
    }
    return count;
  };

  /*
   * 격자를 넣을지는 **기록이 몇 줄 남느냐**로 정한다. 1:1은 세로가 절반이라
   * 격자를 억지로 넣으면 기록이 한두 줄만 남아 타임라인 서비스로 안 보인다.
   */
  const withPicks = pickCells > 0 && take(fits(true)) >= MIN_EVENTS_WITH_PICKS;
  const gridY = footerTop - px(24) - pickHeight;
  const rows = shown.slice(0, take(fits(withPicks)));
  const axisHeight = rows.reduce((sum, row) => sum + rowHeight(row.period), 0);
  const more = rows.length < input.events.length;

  /*
   * 축 선을 **먼저** 긋는다.
   *
   * 처음에는 점을 다 찍은 뒤 `destination-over`로 밑에 깔려고 했는데, 맨 처음
   * 배경을 칠해 둬서 선이 **배경 밑으로** 들어가 아예 안 보였다. 줄 높이를
   * 미리 재서 먼저 긋고, 점을 그 위에 얹는다.
   */
  const axisTop = y - px(6);
  const axisBottom = y + axisHeight - px(14);
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(axis, axisTop);
  ctx.lineTo(axis, axisBottom);
  ctx.stroke();

  let shownYear: number | null = null;

  for (const { event, period } of rows) {
    /* 같은 해는 **한 번만** 찍는다 — 연도는 눈금이지 줄마다 붙는 꼬리표가 아니다 */
    if (event.year !== shownYear) {
      ctx.fillStyle = MUTED;
      ctx.font = `${px(15)}px ${YEAR}`; // --t-body
      ctx.textAlign = 'right';
      ctx.fillText(String(event.year), left + yearColumn, y);
      ctx.textAlign = 'left';
      shownYear = event.year;
    }

    /* 점 둘레를 배경색으로 두른다 — 실제 화면의 `box-shadow: 0 0 0 3px bg`.
       축 선이 점 밑을 지나가는 게 아니라 점에서 끊겨 보여야 같은 그림이다 */
    ctx.fillStyle = BG;
    ctx.beginPath();
    ctx.arc(axis, y - px(4), dotSize / 2 + px(3), 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = input.topicColors.get(event.topicId) ?? MUTED;
    ctx.beginPath();
    ctx.arc(axis, y - px(4), dotSize / 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = INK;
    ctx.font = `600 ${px(13)}px ${BODY}`; // --t-sm. 연도보다 **작다**
    ctx.fillText(clip(ctx, event.title, WIDTH - bodyLeft - left), bodyLeft, y);

    if (period !== null) {
      y += px(16);
      ctx.fillStyle = MUTED;
      ctx.font = `${px(11)}px ${BODY}`; // --t-xs
      ctx.fillText(period, bodyLeft, y);
    }
    y += px(26);
  }

  /*
   * 못 담은 게 있으면 **축을 아래로 흘려보낸다.**
   *
   * "외 91개"라고 적던 자리다. 숫자를 적으면 그림에 설명이 하나 더 붙는데,
   * 끊기지 않고 흐려지는 선이 같은 말을 글자 없이 한다 — 그리고 정확히 몇
   * 개인지는 들어와서 보면 된다. 그게 이 그림의 용건이다.
   */
  if (more) {
    /*
     * 흐려지는 길이는 **남는 자리를 다 쓴다**(최소 FADE). 길이를 고정하면
     * 줄 하나가 안 들어갈 때 그 자리가 통째로 빈 띠로 남는다 — 선이 아래까지
     * 흘러가면 같은 자리가 "계속된다"는 말이 된다.
     */
    const fadeEnd = (withPicks ? gridY : footerTop) - px(12);
    const fade = ctx.createLinearGradient(0, axisBottom, 0, fadeEnd);
    fade.addColorStop(0, LINE);
    /* 같은 색의 **투명**으로 끝낸다 — 색을 또 적으면 테마가 바뀔 때 어긋난다 */
    fade.addColorStop(1, 'transparent');
    ctx.strokeStyle = fade;
    ctx.beginPath();
    ctx.moveTo(axis, axisBottom);
    ctx.lineTo(axis, fadeEnd);
    ctx.stroke();
    for (const [index, alpha] of [0.5, 0.3, 0.15].entries()) {
      ctx.fillStyle = MUTED;
      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(axis, axisBottom + px(8) + index * px(9), dotSize / 2, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  // ── 취향 격자 (`PickGrid.module.css`) ──
  if (withPicks) {
    for (const [index, pick] of input.picks.slice(0, pickCells).entries()) {
      const x = left + index * (pickSize + pickGap);
      ctx.save();
      roundedRect(ctx, x, gridY, pickSize, pickHeight, px(10)); // --r-md
      ctx.clip();
      const image = pick.imageUrl === undefined ? null : await loadImage(pick.imageUrl);
      if (image === null) {
        ctx.fillStyle = SOFT;
        ctx.fillRect(x, gridY, pickSize, pickHeight);
        ctx.strokeStyle = LINE; // .noImage의 테두리
        ctx.lineWidth = 2;
        roundedRect(ctx, x + 1, gridY + 1, pickSize - 2, pickHeight - 2, px(10));
        ctx.stroke();
      } else {
        cover(ctx, image, x, gridY, pickSize, pickHeight);
        /*
         * 사진 위 글자가 묻히지 않게 그늘을 깐다. 정지점이 **셋**인 건
         * 실제 화면과 같다 — 둘만 쓰면 글자가 앉는 높이에서 아직 옅어서
         * 흰 사진 위의 카테고리 줄이 안 읽힌다.
         */
        const shade = ctx.createLinearGradient(0, gridY, 0, gridY + pickHeight);
        shade.addColorStop(0, 'rgba(0,0,0,0)');
        /* 아래에서 46% 지점 — 글자가 앉는 높이까지는 충분히 어두워야 한다 */
        shade.addColorStop(0.54, SCRIM_MID);
        shade.addColorStop(1, SCRIM_STRONG);
        ctx.fillStyle = shade;
        ctx.fillRect(x, gridY, pickSize, pickHeight);
      }
      /* `.caption`: padding 7px, 카테고리 위 이름 아래, 사이 1px */
      const pad = px(7);
      ctx.fillStyle = image === null ? INK : '#ffffff';
      ctx.font = `700 ${px(13)}px ${BODY}`; // .name
      ctx.fillText(
        clip(ctx, pick.itemName, pickSize - pad * 2),
        x + pad,
        gridY + pickHeight - pad - px(3),
      );
      ctx.fillStyle = image === null ? MUTED : 'rgba(255,255,255,0.92)';
      ctx.font = `${px(11)}px ${BODY}`; // .category
      ctx.fillText(
        clip(ctx, pick.categoryName, pickSize - pad * 2),
        x + pad,
        gridY + pickHeight - pad - px(19),
      );
      ctx.restore();
    }
  }

  // ── 바닥: 주소·로고·QR ──
  /*
   * QR을 두는 이유는 하나다. 스토리를 보는 사람은 주소를 **손으로 쳐야**
   * 하는데, 그 한 단계에서 거의 다 떨어진다. 카메라만 대면 되게 한다.
   */
  const qrX = WIDTH - left - qrSize;
  const qrY = HEIGHT - px(24) - qrSize;
  const qr = await loadImage(
    await QRCode.toDataURL(profileUrl(input.profile.handle), {
      margin: 1,
      /* 어두운 그림이지만 QR은 **밝은 바탕에 어두운 점**이어야 잘 읽힌다 */
      color: { dark: '#0d0d0d', light: '#f2f2f2' },
      width: qrSize * 2,
    }),
  );
  if (qr !== null) {
    ctx.save();
    roundedRect(ctx, qrX, qrY, qrSize, qrSize, px(10));
    ctx.clip();
    ctx.drawImage(qr, qrX, qrY, qrSize, qrSize);
    ctx.restore();
  }

  /*
   * 로고가 위, 주소가 아래다. 읽는 순서가 **어디냐 → 어떻게 가냐**여야 한다 —
   * 주소가 먼저 오면 긴 글자가 먼저 눈에 들어와 어느 서비스인지가 묻힌다.
   */
  const logo = logoFont(input.appName);
  ctx.fillStyle = MUTED;
  ctx.font = `${px(16)}px ${logo.family}`;
  ctx.letterSpacing = logo.tracking;
  ctx.fillText(input.appName, left, HEIGHT - px(24) - px(40));
  ctx.letterSpacing = '0px';
  ctx.fillStyle = INK;
  ctx.font = `700 ${px(15)}px ${BODY}`;
  ctx.fillText(profileUrl(input.profile.handle), left, HEIGHT - px(24) - px(12));

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob === null ? reject(new Error('그림을 못 만듭니다')) : resolve(blob)),
      'image/png',
    );
  });
}

/** 칸을 넘치면 말줄임. 넘친 글자가 그림 밖으로 나가면 잘린 채로 굳는다 */
function clip(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) {
    return text;
  }
  let cut = text;
  while (cut.length > 1 && ctx.measureText(`${cut}…`).width > max) {
    cut = cut.slice(0, -1);
  }
  return `${cut}…`;
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** 칸을 꽉 채우고 넘치는 쪽을 자른다(`object-fit: cover`와 같다) */
function cover(
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  w: number,
  h: number,
): void {
  const scale = Math.max(w / image.width, h / image.height);
  const width = image.width * scale;
  const height = image.height * scale;
  ctx.drawImage(image, x + (w - width) / 2, y + (h - height) / 2, width, height);
}

/**
 * 못 읽는 사진은 **건너뛴다** — 한 장이 안 열린다고 그림 전체를 버릴 이유가 없다.
 *
 * ⚠️ `crossOrigin`을 꼭 붙인다. 안 붙이고 남의 출처 사진을 그리면 캔버스가
 * **더럽혀져**(tainted) 내보낼 때 통째로 막힌다 — 그림이 다 그려진 뒤에
 * `toBlob`에서 터지므로, 어디서 틀어졌는지가 한참 뒤에 드러난다.
 *
 * 붙여 두면 CORS가 없는 사진은 **그 한 장만** 못 읽고 넘어간다. 라이브는
 * 사진이 같은 도메인(`life-timeline.com/uploads/…`)이라 영향이 없고,
 * 로컬은 MinIO가 다른 포트라 이 길로 빠진다.
 */
async function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}
